import type { TokenSource } from '@/api/client';

import type { AuthClient } from './client';
import { CALLBACK_PATH } from './settings';

/**
 * The redirect-loop guard (CHG-050). Okta answers a redirect at once when the person already has
 * a session there, so a console that signs in again whenever the API says 401 would bounce for
 * ever if the API kept refusing a fresh token (a wrong audience, a client id the API does not
 * allow, a JWKS outage). So a redirect leaves a mark in sessionStorage, which holds across the page
 * loads it causes, and the console never redirects by itself while the mark is there: it shows
 * "You are signed out." with a Sign in button instead, and the person's own click always goes
 * through. The mark is cleared only when an authorized request succeeds (`authorized`), so a
 * session that ends hours later still gets its one automatic redirect. A time window would loop
 * once a window, which is why there is none.
 */
export const REDIRECT_KEY = 'console.auth.redirected';

// Only where sessionStorage is unavailable: a page-load guard is better than none.
let memoryMark = false;

function marked(): boolean {
  try {
    return window.sessionStorage.getItem(REDIRECT_KEY) !== null;
  } catch {
    return memoryMark;
  }
}

function setMark(on: boolean): void {
  memoryMark = on;
  try {
    if (on) window.sessionStorage.setItem(REDIRECT_KEY, '1');
    else window.sessionStorage.removeItem(REDIRECT_KEY);
  } catch {
    // The in-memory mark above is all there is.
  }
}

/** A redirect this page has started: its calls after the first are the same sign-in, not a loop. */
const redirecting = new WeakSet<AuthClient>();
const refusals = new Set<() => void>();

/** Called each time the guard declines an automatic redirect, so the console can show the button. */
export function onSignInRefused(listener: () => void): () => void {
  refusals.add(listener);
  return () => {
    refusals.delete(listener);
  };
}

/** An authorized request succeeded: the next time the session ends it may redirect once more. */
export function clearRedirectMark(): void {
  setMark(false);
}

/**
 * Start a sign-in at Okta, to come back to `path`. Returns false, and does nothing but tell the
 * `onSignInRefused` listeners, when an earlier redirect has not been followed by an authorized
 * request; a person's own click passes `force`. True means a redirect is under way.
 */
export function redirectToSignIn(client: AuthClient, path: string, force = false): boolean {
  if (redirecting.has(client)) return true;
  if (!force && marked()) {
    refusals.forEach((listener) => listener());
    return false;
  }
  setMark(true);
  redirecting.add(client);
  client.signIn(path).catch((error: unknown) => {
    redirecting.delete(client);
    console.error('Sign-in did not start.', error);
  });
  return true;
}

/** The page the person is on, as a path to come back to; the callback itself is never one. */
export function currentPath(): string {
  const { pathname, search, hash } = window.location;
  return pathname === CALLBACK_PATH ? '/' : `${pathname}${search}${hash}`;
}

/** What the HTTP client asks of sign-in: the token, its renewal, and a guarded redirect. */
export function tokenSource(client: AuthClient): TokenSource {
  return {
    accessToken: () => client.accessToken(),
    renewAccessToken: () => client.renewAccessToken(),
    signInAgain: () => {
      redirectToSignIn(client, currentPath());
    },
    authorized: clearRedirectMark,
  };
}
